<template>
  <Dialog
    title="Artwork Approval"
    @close-event="handleClose"
    show-close-button
    show-footer
    custom-footer
    custom-title
    md
  >
    <!-- Custom Title Slot -->
    <template #title>
      <div class="flex items-center gap-2 w-full">
        <h4 class="text-lg text-black">Artwork Approval</h4>
        <span class="bg-[#9197b3] text-white p-1 rounded">
          {{ activity_navigation_index + 1 }} /
          {{ activity_items.activity_item_data.length }}
        </span>
      </div>
    </template>
    <!-- Header Right Slot: Actions -->
    <template #header-right>
      <div class="flex gap-2 items-center justify-end w-full mr-2">
        <button class="btn text-sm" @click="handleUndo" v-if="!isNullOrEmpty(currentItem.action)">
          <Undo2Icon class="size-4" /> Reset Feedback
        </button>
        <template v-if="currentItem.action">
          <span v-if="currentItem.action === 'accept'" class="badge badge-success">Accepted</span>
          <span v-else class="badge badge-danger">Rejected</span>
        </template>
        <template v-else>
          <div class="inline-flex items-stretch rounded-md overflow-hidden" role="group" aria-label="Accept options">
            <button type="button" class="btn rounded-r-none" @click="handleDecision('accept')">
              Accept
            </button>
            <Dropdown
              :customClass="'!w-auto'"
              triggerStyle="flex h-full items-center justify-center px-2 btn rounded-l-none border-l border-gray-300/40"
            >
              <template #trigger>
                <ChevronRightIcon class="text-[#686666] size-4" />
              </template>
              <template #contents>
                <ul class="urgency-dropdown">
                  <li
                    class="flex items-center justify-start gap-2 px-5 py-2 text-center duration-300 cursor-pointer group hover:bg-gray-100"
                    @click="handleDecision('accept-all')"
                  >
                    <p class="flex flex-row items-center justify-between gap-x-4">
                      <span>Accept All</span>
                    </p>
                  </li>
                </ul>
              </template>
            </Dropdown>
          </div>
          <button v-if="!isFactoryReview" class="btn" @click="handleDecision('reject')">Reject</button>
          <button
            v-else
            class="btn"
            @click="handleDecision('reject')"
            :disabled="isFactoryReview && order?.additional_fields?.is_manual_order"
            :title="
              isFactoryReview && order?.additional_fields?.is_manual_order
                ? 'Manual Order Cannot Be Rejected'
                : ''
            "
          >
            Reject
          </button>
        </template>
      </div>
    </template>
    <template #modal-content>
      <!-- TEST SAMPLE MODE (DEFAULT) -->
      <template v-if="!isFactoryReview">
        <!-- IMAGES -->
        <div class="flex items-center justify-content-between gap-2 py-4 px-3">
          <button
            class="btn cursor-pointer self-center"
            v-if="hasMultipleSamples"
            @click="navigateSampleFiles('back')"
            :disabled="activity_navigation_index === 0"
          >
            <ChevronLeft class="size-5" />
          </button>
          <!-- Show all files (typically 2 images per sample) -->
          <div v-for="(actFile, fileInd) in currentItem.files" :key="fileInd" class="w-1/2 px-2">
            <img
              :src="actFile.file"
              class="w-full border border-gray-300 rounded"
              style="max-height: 500px"
            />
          </div>

          <button
            class="btn cursor-pointer self-center"
            v-if="hasMultipleSamples"
            @click="navigateSampleFilesGuard('next')"
            :disabled="activity_navigation_index === activity_items.activity_item_data.length - 1"
          >
            <ChevronRight class="size-5" />
          </button>
        </div>

        <!-- FEEDBACK -->
        <div class="p-4">
          <div class="fs-4">Write your feedback</div>
          <textarea
            :class="[
              'mt-2 w-full border rounded px-3 py-2',
              rejectValidationError ? 'border-red-500' : 'border-gray-300',
            ]"
            v-model="currentItem.message"
            placeholder="Please write your feedback here..."
            rows="5"
          ></textarea>

          <!-- ERROR: Reject requires feedback -->
          <div v-if="rejectValidationError" class="text-red-600 mt-1">
            Feedback is required when rejecting.
          </div>
        </div>
      </template>

      <!-- FACTORY REVIEW MODE -->
      <template v-else>
        <div
          v-if="
            activity_items.activity_item_data[activity_navigation_index]"
          class="flex items-center justify-content-between gap-1 py-4 px-3 m-auto"
        >
          <div class="fs-5">
            <button
              class="btn cursor-pointer self-center"
              v-if="hasMultipleSamples"
              @click="navigateSampleFiles('back')"
              :disabled="activity_navigation_index === 0"
            >
              <ChevronLeft class="size-5" />
            </button>
          </div>

          <div
            v-for="(actFile, fileInd) in activity_items.activity_item_data[activity_navigation_index].files"
            :key="`actfile-${fileInd}`"
            class="w-1/2 px-2"
          >
            <img
              @click="!activity_items.activity_item_data[activity_navigation_index].action ? '' : ''"
              :src="`${actFile.file}`"
              alt=""
              class="w-full border border-gray-300 rounded"
              style="max-height: 500px"
              crossorigin="anonymous"
            />
          </div>

          <div class="fs-5" v-if="activity_items.activity_item_data.length - 1 !== activity_navigation_index">
            <button
              class="btn cursor-pointer self-center"
              v-if="hasMultipleSamples"
              @click="navigateSampleFilesGuard('next')"
              :disabled="activity_navigation_index === activity_items.activity_item_data.length - 1"
            >
              <ChevronRight class="size-5" />
            </button>
          </div>
        </div>

        <!-- BATCH DOWNLOAD -->
        <div class="flex justify-center gap-2">
          <button
            class="btn text-sm"
            v-if="!batch_loading"
            @click.prevent="batchDownloadAssets(activity_items, activity_navigation_index)"
          >
            Batch Assets Download
          </button>
          <button class="btn text-sm" v-else>
            <span class="spinner-border"></span>
          </button>
        </div>

        <!-- FEEDBACK -->
        <div class="p-2">
          <div class="fs-4">Write your feedback</div>
          <div class="mt-2">
            <textarea
              :class="[
                'w-full border rounded px-3 py-2',
                rejectValidationError ? 'border-red-500' : 'border-gray-300',
              ]"
              v-model="activity_items.activity_item_data[activity_navigation_index].message"
              placeholder="Please write your feedback here..."
              rows="5"
            ></textarea>
          </div>
          <!-- ERROR: Reject requires feedback -->
          <div v-if="rejectValidationError" class="text-red-600 mt-1">
            Feedback is required when rejecting.
          </div>
        </div>
      </template>
    </template>

    <!-- FOOTER -->
    <template #footer>
      <div class="flex justify-end gap-2 items-center">
        <button class="btn text-sm" @click="handleUndo" v-if="!isNullOrEmpty(currentItem.action)">
          <Undo2Icon class="size-4" /> Reset Feedback
        </button>
        <template v-if="currentItem.action">
          <span v-if="currentItem.action === 'accept'" class="badge badge-success">Accepted</span>
          <span v-else class="badge badge-danger">Rejected</span>
        </template>
        <template v-else>
          <div class="inline-flex items-stretch rounded-md overflow-hidden" role="group" aria-label="Accept options">
            <button type="button" class="btn rounded-r-none" @click="handleDecision('accept')">
              Accept
            </button>
            <Dropdown
              :customClass="'!w-auto'"
              triggerStyle="flex h-full items-center justify-center px-2 btn rounded-l-none border-l border-gray-300/40"
            >
              <template #trigger>
                <ChevronRightIcon class="text-[#686666] size-4" />
              </template>
              <template #contents>
                <ul class="urgency-dropdown">
                  <li
                    class="flex items-center justify-start gap-2 px-5 py-2 text-center duration-300 cursor-pointer group hover:bg-gray-100"
                    @click="handleDecision('accept-all')"
                  >
                    <p class="flex flex-row items-center justify-between gap-x-4">
                      <span>Accept All</span>
                    </p>
                  </li>
                </ul>
              </template>
            </Dropdown>
          </div>
          <button
            v-if="!isFactoryReview"
            class="btn"
            @click="handleDecision('reject')"
          >
            Reject
          </button>
          <button
            v-else
            class="btn"
            @click="handleDecision('reject')"
            :disabled="isFactoryReview && order?.additional_fields?.is_manual_order"
            :title="
              isFactoryReview && order?.additional_fields?.is_manual_order
                ? 'Manual Order Cannot Be Rejected'
                : ''
            "
          >
            Reject
          </button>
        </template>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import Dropdown from '@/components/Shared/Dropdown.vue';
import Dialog from '@/components/Shared/Dialog.vue';
import { isNullOrEmpty } from '@/composables';
import { showToast } from '@/composables/swal';
import { http } from '@/interceptors/axiosRequestInterceptor';
import { ChevronLeft, ChevronRight, ChevronRightIcon, Undo2Icon } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';

const props = withDefaults(defineProps<{
  activity_items: any;
  activity_navigation_index: number;
  modal_type?: string;
  order?: any;
}>(), {
  modal_type: 'test_sample',
  order: null,
});

// Get API URL from environment
const apiUrl = computed(() => import.meta.env.VITE_API_BASE_URL || '');

const emits = defineEmits(['close', 'navigate-sample-files', 'submit-sample-files']);

// Current item being reviewed
const currentItem = computed(() => {
  return props.activity_items.activity_item_data[props.activity_navigation_index];
});

// Samples carousel (between activity items)
const hasMultipleSamples = computed(() => props.activity_items.activity_item_data.length > 1);

// Check if factory_review mode
const isFactoryReview = computed(() => props.modal_type === 'factory_review');

// Batch loading state
const batch_loading = ref(false);

// CLICK HANDLERS
const handleClose = () => emits('close');

// Accept / Reject with validation and auto-advance
const rejectValidationError = ref(false);
const handleUndo = () => {
  currentItem.value.action = '';
  currentItem.value.message = '';
  rejectValidationError.value = false;
};
const handleDecision = (type) => {
  if (type === 'accept') {
    currentItem.value.action = 'accept';
    currentItem.value.message = '';
    rejectValidationError.value = false;
    autoAdvanceNext();
    return;
  }

  if (type === 'reject') {
    if (!currentItem.value.message || !currentItem.value.message.trim().length) {
      rejectValidationError.value = true;
      return; // do not mark as rejected
    }
    currentItem.value.action = 'reject';
    rejectValidationError.value = false;
    autoAdvanceNext();
  }

  if (type === 'accept-all') {
    props.activity_items.activity_item_data.forEach(item => {
      item.action = 'accept'
      item.message = ''
    })
    rejectValidationError.value = false;
    emits('submit-sample-files');
    return
  }
};
// Clear validation error once feedback is provided
watch(
  () => currentItem.value?.message,
  (val) => {
    if (val && val.trim().length) rejectValidationError.value = false;
  }
);

// Disable next unless valid
const isNextDisabled = computed(() => {
  if (!currentItem.value.action) return true; // must accept or reject

  if (currentItem.value.action === 'reject' && !currentItem.value.message) return true; // reject requires feedback

  return false;
});

// Navigation guards
const navigateSampleFilesGuard = (dir) => {
  if (dir === 'next' && isNextDisabled.value) {
    if (!currentItem.value.action) {
      showToast('Please accept or reject this sample to continue.', 'error');
      return;
    }
    if (currentItem.value.action === 'reject' && !currentItem.value.message) {
      rejectValidationError.value = true;
      showToast('Feedback is required to reject this sample.', 'error');
      return;
    }
  }
  emits('navigate-sample-files', dir);
};
const navigateSampleFiles = (dir) => {
  emits('navigate-sample-files', dir, false, true);
};

// Auto advance helper
const autoAdvanceNext = () => {
  const lastIndex = props.activity_items.activity_item_data.length - 1;
  if (props.activity_navigation_index === lastIndex) {
    emits('submit-sample-files');
  } else {
    emits('navigate-sample-files', 'next', false, true);
  }
};

// Batch download assets function
const getCurrentDatetime = () => {
  const now = new Date();
  return now.toISOString().slice(0, 10); // Returns YYYY-MM-DD format
};

const batchDownloadAssets = async (activityItems = null, activityIndex = null) => {
  if (!props.order || !props.order.id) {
    showToast('Order information is missing', 'error');
    return;
  }

  // find the factoryProduct (same logic you already have)
  let factoryProduct = null;
  if (activityItems && activityIndex !== null && activityIndex !== -1) {
    const activityItem = activityItems.activity_item_data[activityIndex];
    if (!activityItem || !activityItem.factory_product_id) {
      showToast('Factory product information is missing', 'error');
      return;
    }
    const orderItem = props.order.items?.[activityItems.order_item_index];
    if (orderItem && orderItem.factory_products) {
      factoryProduct = orderItem.factory_products.find((fp: any) => fp.id === activityItem.factory_product_id);
    }
  }
  if (!factoryProduct) {
    showToast('Factory product not found', 'error');
    return;
  }

  batch_loading.value = true;
  try {
    const url = `/order/${props.order.id}/product/${factoryProduct.id}/extract-zip`;
    const resp = await http.get(url, { responseType: 'blob' });
    const contentType: string = (resp.headers?.['content-type'] || resp.headers?.['Content-Type'] || 'application/octet-stream') as string;
 
    const blob = new Blob([resp.data], { type: contentType });
    const downloadUrl = window.URL.createObjectURL(blob);

    const safeName = (props.order.order_no
      ? `order_${props.order.order_no}_product_${factoryProduct.product_name}`
      : `order_${getCurrentDatetime()}_product_${factoryProduct.product_name}`
    );

    // choose extension based on contentType (if zip -> .zip, if excel -> .xlsx)
    let ext = '.zip';
    if (/excel|spreadsheet|xlsx/i.test(contentType)) ext = '.xlsx';
    if (/zip/i.test(contentType)) ext = '.zip';
    if (/octet-stream/i.test(contentType)) ext = '.zip';

    const filename = `${safeName}${ext}`;

    const a = document.createElement('a');
    a.href = downloadUrl;
    a.setAttribute('download', filename);
    document.body.appendChild(a);
    a.click();
    a.remove();

    // cleanup
    window.URL.revokeObjectURL(downloadUrl);

    showToast('Download started', 'success');
  } catch (err) {
    showToast('Error downloading assets. See console for details.', 'error');
    console.error('batchDownloadAssets error', err);
  } finally {
    batch_loading.value = false;
  }
};
</script>
<style scoped lang="scss">
.btn {
  @apply px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-md flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50;
}

.badge {
  @apply px-3 py-2 rounded-md flex items-center gap-2 text-sm;
}
.badge-warning {
  background: #f9b115;
  color: #4f5d73;
}
.badge-success {
  background: #2eb85c;
  color: white;
}
.badge-danger {
  background: #e55353;
  color: white;
}
</style>
